import { FaStar, FaHeart } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useCartStore } from '../context/CartStore';
import { useFavoritesStore } from '../context/FavoritesStore';
import { toast } from 'react-toastify';
import AuthModal from './AuthModal';
import useProtectedAction from '../hooks/useProtectedAction';
import { motion } from 'framer-motion';

const ProductCard = ({ product }) => {
  const addItem = useCartStore((state) => state.addItem);
  const favorites = useFavoritesStore((state) => state.favorites);
  const addFavorite = useFavoritesStore((state) => state.addFavorite);
  const removeFavorite = useFavoritesStore((state) => state.removeFavorite);

  const { executeAction, authModalOpen, setAuthModalOpen, actionType } = useProtectedAction();

  const isFavorited = favorites.some((fav) => fav._id === product._id);

  const handleAddToCart = () => {
    executeAction(() => {
      addItem(product);
      toast.success('Added to collection ✨');
    }, 'cart');
  };

  const handleToggleFavorite = () => {
    executeAction(() => {
      if (isFavorited) {
        removeFavorite(product._id);
        toast.info('Removed from favorites');
      } else {
        addFavorite(product);
        toast.success('Added to favorites ✨');
      }
    }, 'favorites');
  };

  return (
    <>
      <motion.div 
        whileHover={{ y: -8 }}
        className="bg-surfaceDark border border-white/5 overflow-hidden h-full flex flex-col group relative"
      >
        <div className="relative h-64 sm:h-80 overflow-hidden bg-[#0d0d0d]">
          <Link to={`/product/${product._id}`} className="absolute inset-0 z-0">
            <img
              src={product.images?.[0] || '/placeholder.svg'}
              alt={product.name}
              className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 ease-in-out"
            />
          </Link>
          <span className="absolute top-3 right-3 bg-deepDark/80 backdrop-blur-md border border-white/10 text-white px-3 py-1 text-[10px] font-bold tracking-widest uppercase z-10 pointer-events-none">
            {product.material}
          </span>
          {product.discount > 0 && (
            <span className="absolute top-3 left-3 bg-gold text-deepDark px-3 py-1 font-bold text-[10px] tracking-widest uppercase z-10 pointer-events-none">
              -{product.discount}%
            </span>
          )}
          <button
            className={`absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 z-10 backdrop-blur-sm ${isFavorited
              ? 'bg-gold text-deepDark border-none scale-110'
              : 'bg-deepDark/50 border border-white/20 text-white hover:bg-gold hover:text-deepDark hover:border-gold'
              }`}
            onClick={handleToggleFavorite}
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <FaHeart className="text-sm" />
          </button>
        </div>

        <div className="p-5 flex flex-col flex-1 bg-gradient-to-t from-deepDark to-surfaceDark">
          <Link to={`/product/${product._id}`} className="hover:text-gold transition-colors duration-300 block w-fit">
            <h3 className="text-lg font-serif text-white mb-2 line-clamp-2 min-h-14 tracking-wide">{product.name}</h3>
          </Link>

          <div className="flex items-center gap-1 mb-3">
            {[...Array(5)].map((_, i) => (
              <FaStar
                key={i}
                className={`text-[10px] ${i < Math.floor(product.rating) ? 'text-gold' : 'text-white/20'}`}
              />
            ))}
            <span className="text-[10px] text-textMuted ml-2 font-light tracking-widest uppercase">({product.reviews?.length || 0})</span>
          </div>

          <p className="text-textMuted text-xs font-light tracking-wide leading-relaxed mb-4 line-clamp-2">{product.description?.substring(0, 80)}...</p>

          <div className="flex gap-3 items-baseline mb-4">
            <span className="text-xl font-serif text-gold">₹{product.price.toLocaleString()}</span>
            {product.discount > 0 && (
              <span className="text-xs text-textMuted line-through font-light tracking-wider">
                ₹{Math.round(product.price / (1 - product.discount / 100)).toLocaleString()}
              </span>
            )}
          </div>

          <div className="mb-6">
            {product.stock > 0 ? (
              <span className="text-[10px] tracking-widest uppercase font-bold text-white/50">Available</span>
            ) : (
              <span className="text-[10px] tracking-widest uppercase font-bold text-red-500/70">Waitlist</span>
            )}
          </div>

          <button
            className="w-full bg-transparent border border-gold text-gold font-bold py-3 text-xs tracking-widest uppercase hover:bg-gold hover:text-deepDark disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gold disabled:cursor-not-allowed transition-all duration-300 mt-auto"
            onClick={handleAddToCart}
            disabled={product.stock === 0}
          >
            {product.stock > 0 ? 'Acquire Piece' : 'Join Waitlist'}
          </button>
        </div>
      </motion.div>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        actionType={actionType}
      />
    </>
  );
};

export default ProductCard;